import { Select } from '@mantine/core';
import { IconAlertTriangle, IconArrowRight, IconInfoCircle, IconShip } from '@tabler/icons-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDay } from '../../format.js';
import { StatusBadge } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { EMPTY_HISTORY_FILTER, historySearch } from '../forms/history.js';
import { FORM_HISTORY_PATH, recordPath } from '../forms/record-model.js';
import type { FormRecordSource } from '../forms/source.js';
import type { FormRecordTreeView } from '../forms/types.js';
import classes from './Documents.module.css';
import {
  chooseRecordVessel,
  type RecordChild,
  type RecordsState,
  recallRecordVessel,
  recordRevision,
  recordTreeBadge,
  rememberRecordVessel,
} from './tree-records.js';
import type { LibrarySection } from './types.js';
import { COMPANY_DOCUMENTS } from './vessel-library.js';

/**
 * ISM ağacında formun kayıtları (ADR 0072): ağaç bileşeninin kayıt düzeyi. Form düğümü açılınca
 * kayıtlar form başına tembel okunur ve önbellekte kalır; kayıt olayında (gemide SSE ve senkron)
 * açık formlar yeniden okunur.
 */
export interface TreeRecords {
  /**
   * Önbellek anahtarı: gemide sabit, ofiste seçilen gemi. `null`: ofiste gemi seçilmedi; kayıt
   * düzeyi yok, açık formda "Select a vessel to see records" ipucu.
   */
  key: string | null;
  load(documentId: string): Promise<FormRecordTreeView>;
  subscribe?(listener: () => void): () => void;
  openRecord(recordId: string): void;
  /** "All (n) →": formun kayıt listesi (Form History form süzgeciyle). */
  openAll(documentId: string): void;
  /** Ağacın üstündeki gemi seçici (ofiste şirket kütüphanesinde). */
  picker?: ReactNode;
  /** Birim testi: açık formlar ve okunmuş kayıtlar. */
  initial?: { open: readonly string[]; states: Readonly<Record<string, RecordsState>> };
}

/**
 * Açık formların kayıt durumu. Anahtar `<gemi>|<form>`: gemi değişince eski kayıtlar görünmez,
 * yeniden okunur. Aynı turda bir form bir kez istenir; geç dönen eski yanıt yenisini ezmez.
 */
export function useTreeRecordStates(
  records: TreeRecords | undefined,
  open: ReadonlySet<string>,
): { state(documentId: string): RecordsState | null | undefined; retry(): void } {
  const key = records?.key ?? null;
  const [states, setStates] = useState<ReadonlyMap<string, RecordsState>>(
    () =>
      new Map(
        Object.entries(records?.initial?.states ?? {}).map(([documentId, value]) => [
          `${key}|${documentId}`,
          value,
        ]),
      ),
  );
  const [round, setRound] = useState(0);
  const requested = useRef(new Map<string, number>());
  const load = records?.load;
  const subscribe = records?.subscribe;

  useEffect(() => {
    if (!load || key === null) return;
    for (const documentId of open) {
      const stateKey = `${key}|${documentId}`;
      if (requested.current.get(stateKey) === round) continue;
      requested.current.set(stateKey, round);
      // Okunmuş kayıtlar yenilenirken görünür kalır; yalnız ilk okumada "Loading"
      setStates((current) =>
        current.get(stateKey)?.kind === 'ready'
          ? current
          : new Map(current).set(stateKey, { kind: 'loading' }),
      );
      const ticket = round;
      load(documentId).then(
        (view) => {
          if (requested.current.get(stateKey) !== ticket) return;
          setStates((current) => new Map(current).set(stateKey, { kind: 'ready', view }));
        },
        () => {
          if (requested.current.get(stateKey) !== ticket) return;
          setStates((current) =>
            current.get(stateKey)?.kind === 'ready'
              ? current
              : new Map(current).set(stateKey, { kind: 'error' }),
          );
        },
      );
    }
  }, [load, key, open, round]);

  useEffect(() => subscribe?.(() => setRound((value) => value + 1)), [subscribe]);

  return {
    state: (documentId) => (key === null ? null : states.get(`${key}|${documentId}`)),
    retry: () => setRound((value) => value + 1),
  };
}

/** Formun altındaki satırın treeitem'i (kayıt, "All (n)" ya da tek satırlık durum). */
export function RecordChildItem({
  child,
  level,
  labelId,
  tabIndex,
  roles,
  itemRef,
  onFocus,
  onActivate,
}: {
  child: RecordChild;
  level: number;
  labelId: string;
  tabIndex: number;
  roles: Readonly<Record<string, string>>;
  itemRef: (element: HTMLDivElement | null) => void;
  onFocus: () => void;
  onActivate: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.tree.records.${key}`, options ?? {});
  const inert = child.kind === 'hint' || child.kind === 'loading' || child.kind === 'empty';
  let icon: ReactNode = null;
  let label: ReactNode;
  let title: string;
  let badges: ReactNode = null;
  switch (child.kind) {
    case 'record': {
      const { item } = child;
      const day = formatDay(item.recordDay);
      const revision = recordRevision(item);
      const badge = recordTreeBadge(t, item, roles);
      // İkonsuz: dar ağaçta kayıt no ve gün tek satıra sığar; "All (n)" ile hizalı
      title = [item.recordNo, day, item.title].filter(Boolean).join(' · ');
      label = (
        <>
          <span className={classes.treeRecordNo}>{item.recordNo}</span>{' '}
          <span className={classes.treeRecordDay}>{day}</span>
        </>
      );
      badges = (
        <>
          {revision ? <span className={classes.treeRev}>{revision}</span> : null}
          <span title={badge.label} className={classes.treeRecordBadge}>
            <StatusBadge tone={badge.tone}>
              <span className={classes.treeBadgeText}>
                {badge.waiting ? <span aria-hidden="true">⏳ </span> : null}
                {badge.label}
              </span>
            </StatusBadge>
          </span>
        </>
      );
      break;
    }
    case 'all':
      title = k('allLabel', { count: child.total });
      label = (
        <span className={classes.treeAll}>
          {k('all', { count: child.total })}{' '}
          <IconArrowRight size={12} stroke={2} aria-hidden className={classes.treeAllIcon} />
        </span>
      );
      break;
    case 'hint':
      icon = <IconShip size={14} stroke={1.6} className={classes.treeDocIcon} aria-hidden />;
      title = k('selectVessel');
      label = title;
      break;
    case 'error':
      icon = (
        <IconAlertTriangle size={14} stroke={1.8} className={classes.treeErrorIcon} aria-hidden />
      );
      title = k('error');
      label = title;
      break;
    default:
      icon =
        child.kind === 'empty' ? (
          <IconInfoCircle size={14} stroke={1.6} className={classes.treeDocIcon} aria-hidden />
        ) : null;
      title = k(child.kind);
      label = title;
  }
  return (
    <div
      role="treeitem"
      aria-level={level}
      aria-labelledby={badges ? `${labelId} ${labelId}-badges` : labelId}
      aria-disabled={inert || undefined}
      tabIndex={tabIndex}
      className={classes.treeItem}
      ref={itemRef}
      onFocus={(event) => {
        if (event.target === event.currentTarget) onFocus();
      }}
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: klavye ağaç düzeyinde */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: treeitem'in görsel satırı */}
      <div
        className={classes.treeRow}
        data-kind="record"
        data-state={child.kind}
        style={{ paddingInlineStart: 8 + (level - 1) * 18 + 6 }}
        onClick={() => {
          onFocus();
          if (!inert) onActivate();
        }}
      >
        {icon}
        <span id={labelId} className={classes.treeLabel} title={title}>
          {label}
        </span>
        {badges ? (
          <span id={`${labelId}-badges`} className={classes.treeBadges}>
            {badges}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** Ofiste ağacın üstündeki gemi seçici (ADR 0072): son seçim bu cihazda hatırlanır. */
export function RecordVesselPicker({
  vessels,
  value,
  onChange,
}: {
  vessels: readonly { id: string; name: string }[];
  value: string | null;
  onChange: (vesselId: string | null) => void;
}) {
  const { t } = useTranslation();
  const data = useMemo(() => vessels.map((v) => ({ value: v.id, label: v.name })), [vessels]);
  return (
    <Select
      className={classes.treeVessel}
      size="sm"
      data={data}
      value={value}
      onChange={onChange}
      clearable
      allowDeselect={false}
      leftSection={<IconShip size={16} aria-hidden />}
      placeholder={t('documents.tree.records.pickVessel')}
      aria-label={t('documents.tree.records.vessel')}
      comboboxProps={{ withinPortal: true }}
    />
  );
}

/**
 * Kütüphane ekranının kayıt düzeyi kaynağı (ADR 0072): yalnız ISM Forms'ta ve kaynak ağaç ucunu
 * biliyorsa. Gemide geminin kayıtları; ofiste bir geminin kütüphanesinde o gemininkiler, şirket
 * kütüphanesinde ağacın üstündeki seçicide seçilen geminin (seçilmediyse ipucu).
 */
export function useLibraryTreeRecords(input: {
  section: LibrarySection;
  forms: FormRecordSource | undefined;
  /** Ofiste adresteki kütüphane (`?library=`): şirket ya da gemi kimliği. */
  library: string | null;
}): TreeRecords | undefined {
  const shell = useShell();
  const navigate = shell?.navigate;
  const { forms, section, library } = input;
  const enabled = section === 'ism' && Boolean(forms?.tree);
  const office = forms?.scope === 'web';
  // Ofiste gemi kütüphanesi: kayıtlar o geminin; şirket kütüphanesinde seçici
  const libraryVessel = office && library && library !== COMPANY_DOCUMENTS ? library : null;
  const picking = enabled && office && libraryVessel === null;
  const [vessels, setVessels] = useState<{ id: string; name: string }[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const listVessels = forms?.office?.vessels;
  useEffect(() => {
    if (!picking || !listVessels) return;
    let live = true;
    void listVessels().then(
      (list) => {
        if (!live) return;
        setVessels(list);
        setChosen(chooseRecordVessel(recallRecordVessel(), list));
      },
      () => live && setVessels([]),
    );
    return () => {
      live = false;
    };
  }, [picking, listVessels]);

  const vesselId = office ? (libraryVessel ?? chosen) : 'ship';
  const tree = forms?.tree;
  const subscribe = forms?.subscribe;
  return useMemo(() => {
    if (!enabled || !tree) return undefined;
    const picker = picking ? (
      <RecordVesselPicker
        vessels={vessels ?? []}
        value={chosen}
        onChange={(next) => {
          setChosen(next);
          rememberRecordVessel(next);
        }}
      />
    ) : undefined;
    return {
      key: vesselId,
      load: (documentId: string) =>
        office && vesselId ? tree(documentId, vesselId) : tree(documentId),
      ...(subscribe ? { subscribe } : {}),
      openRecord: (recordId: string) => navigate?.(recordPath(recordId)),
      openAll: (documentId: string) =>
        navigate?.(
          `${FORM_HISTORY_PATH}${historySearch({
            ...EMPTY_HISTORY_FILTER,
            form: documentId,
            vessel: office ? vesselId : null,
          })}`,
        ),
      ...(picker ? { picker } : {}),
    };
  }, [enabled, tree, office, picking, vesselId, chosen, vessels, subscribe, navigate]);
}
